/**
 * F104 (Pavel 2026-08-10): "vzdycky v tom danem kroku by ten partak mel byt
 * schopny ovladat ten stav na strane klienta." One catalog answers, for every
 * rail step, which client-side states the staff can REOPEN (cancel the
 * client's approval and hand the material back for comments) or APPROVE on
 * the client's behalf (they settled it on a call) - and which states are
 * LOCKED against any override, each with a named reason. The worker route
 * enforces this catalog; the partner console renders it. Neither side may
 * keep its own copy.
 */

/** Client states the staff may override. Mirrors CONFIRMATION_UNDO_KINDS
 *  plus the states VIS2-32 never covered. */
export const STAFF_OVERRIDE_KINDS = [
  "profile", "apple", "q1_submit", "q2_submit", "knowhow_submit",
  "video_onboarding", "video_marketing",
  "strategy", "salespage", "emails", "webinar"
] as const;
export type StaffOverrideKind = (typeof STAFF_OVERRIDE_KINDS)[number];

/**
 * Client states NO override may touch, each with its named reason (F44).
 * These protect paid or irreversible acts that live OUTSIDE our database:
 * a transferred Telegram bot cannot be pulled back by a D1 write, a
 * published site sits on the client's own Netlify account, a connected
 * Stripe moves real money, a finished handover is a business fact
 * ("dokoncene stavy se nevraceji"), and a released app is in Apple's store.
 */
export const STAFF_OVERRIDE_LOCKED_KINDS = [
  "bot_transfer", "publication", "stripe", "handover_close", "app_release"
] as const;
export type StaffOverrideLockedKind = (typeof STAFF_OVERRIDE_LOCKED_KINDS)[number];

export type StaffOverrideAction = "reopen" | "approve";

export interface StaffOverrideEntry {
  action: "reopened" | "approved";
  reason: string;
  by: string;
  by_name: string;
  at: string;
}

/** The staff_overrides client resource: one last-action note per kind. */
export type StaffOverridesValue = Partial<Record<StaffOverrideKind, StaffOverrideEntry>>;

interface OverrideSpec {
  kind: StaffOverrideKind;
  /** Which actions exist for this state. A missing action is not drawn as a
   *  control; `reopenUnavailableKey` names why when only approve exists. */
  actions: StaffOverrideAction[];
  reopenUnavailableKey?: string;
}

/**
 * O8 (audit F115, 2026-08-11): three steps rendered NO client-state panel at
 * all - Trénink, Beta testování and Zpětná vazba - because their spec list is
 * empty and their one lock (bot_transfer) is only true once the bot really
 * moved (F112). Pavel's instruction was "u každé té části", so the panel now
 * says out loud why there is nothing to hand back on those steps instead of
 * disappearing, which reads as a missing feature.
 */
const STEP_EMPTY_REASONS: Record<string, string> = {
  training: "staffOverride.nothing.training",
  beta: "staffOverride.nothing.beta",
  feedback: "staffOverride.nothing.feedback"
};

export function staffOverrideEmptyReasonKey(step: string): string | null {
  return STEP_EMPTY_REASONS[step] ?? null;
}

/** Rail step key -> overridable client states + locked notes on that step. */
const STEP_OVERRIDES: Record<string, { specs: OverrideSpec[]; locked: StaffOverrideLockedKind[] }> = {
  onboarding_call: {
    // A video step has no client approval to revert: the client closes it by
    // watching, and the recorded watch could never be "unwatched" honestly.
    specs: [{ kind: "video_onboarding", actions: ["approve"], reopenUnavailableKey: "staffOverride.noReopen.video" }],
    locked: []
  },
  q1: { specs: [{ kind: "q1_submit", actions: ["reopen", "approve"] }], locked: [] },
  knowhow: { specs: [{ kind: "knowhow_submit", actions: ["reopen", "approve"] }], locked: [] },
  training: { specs: [], locked: [] },
  beta: { specs: [], locked: ["bot_transfer"] },
  feedback: { specs: [], locked: ["bot_transfer"] },
  marketing_call: {
    specs: [{ kind: "video_marketing", actions: ["approve"], reopenUnavailableKey: "staffOverride.noReopen.video" }],
    locked: []
  },
  q2: { specs: [{ kind: "q2_submit", actions: ["reopen", "approve"] }], locked: [] },
  strategy: { specs: [{ kind: "strategy", actions: ["reopen", "approve"] }], locked: [] },
  salespage: { specs: [{ kind: "salespage", actions: ["reopen", "approve"] }], locked: ["publication", "stripe"] },
  emails: { specs: [{ kind: "emails", actions: ["reopen", "approve"] }], locked: [] },
  webinar: { specs: [{ kind: "webinar", actions: ["reopen", "approve"] }], locked: [] },
  handover: { specs: [], locked: ["handover_close", "bot_transfer"] },
  apple: { specs: [{ kind: "apple", actions: ["reopen", "approve"] }], locked: [] },
  app_questionnaire: { specs: [], locked: [] },
  app_release: { specs: [], locked: ["app_release"] },
  profile: { specs: [{ kind: "profile", actions: ["reopen", "approve"] }], locked: [] }
};

export function staffOverridesForStep(step: string): { specs: OverrideSpec[]; locked: StaffOverrideLockedKind[] } {
  return STEP_OVERRIDES[step] ?? { specs: [], locked: [] };
}

/** What the surface knows about the acts these locks protect. */
export interface StaffOverrideLockFacts {
  /** `bot_transfer_state.state`; only 'transferred' means Telegram really
   *  moved the bot. */
  botTransferState?: string | null;
}

/**
 * F112: a lock is a STATEMENT ABOUT REALITY, so it may only be printed once
 * reality matches it. "Predani Telegram bota vratit nejde: bot uz zmenil
 * majitele" stood on the beta step of a client whose transfer had FAILED - the
 * bot had not moved an inch, and the sentence was the partner's only answer on
 * the screen where they came to unstick it. The other four locks guard acts
 * the database can see from their own step (a published site, a connected
 * Stripe, a closed handover, a released app) and stay unconditional.
 */
export function staffOverrideLockApplies(
  kind: StaffOverrideLockedKind,
  facts: StaffOverrideLockFacts
): boolean {
  if (kind === "bot_transfer") return facts.botTransferState === "transferred";
  return true;
}

export function visibleStaffOverrideLocks(
  step: string,
  facts: StaffOverrideLockFacts
): StaffOverrideLockedKind[] {
  return staffOverridesForStep(step).locked.filter((kind) => staffOverrideLockApplies(kind, facts));
}

/**
 * B3 (audit F115, 2026-08-11): "Vrátit klientovi" stood on the q1 step as an
 * available action; only after the partner had written a reason and confirmed
 * did the server answer "Trénink už běží nebo je hotový, tenhle krok vrátit
 * nejde." The refusal is right and its wording is right - the moment is wrong.
 *
 * These are the SAME predicates the worker enforces in
 * `routes/staff-confirmations.ts` (`q1_training_started`,
 * `q2_deliverable_exists`, `q2_build_running`), so the console can close the
 * control BEFOREHAND with the reason beside it, which is the project's own law
 * for a control that cannot do its job (fix-partak-ovladace P-18).
 *
 * A fact the surface cannot see is `undefined` and closes nothing: an unknown
 * state must never invent a lock.
 */
export interface StaffOverrideReopenFacts {
  /** q1: a trial link exists, or the training step has left `locked`. */
  trainingStarted?: boolean;
  /** q2: any sales material row exists (plan / salespage / emails / webinar). */
  salesMaterialExists?: boolean;
  /** q2: a DFY build is running right now. */
  salesBuildRunning?: boolean;
}

export function staffOverrideReopenBlockedKey(
  kind: StaffOverrideKind,
  facts: StaffOverrideReopenFacts
): string | null {
  if (kind === "q1_submit" && facts.trainingStarted === true) {
    return "staffOverride.noReopen.q1TrainingStarted";
  }
  if (kind === "q2_submit") {
    if (facts.salesMaterialExists === true) return "staffOverride.noReopen.q2MaterialExists";
    if (facts.salesBuildRunning === true) return "staffOverride.noReopen.q2BuildRunning";
  }
  return null;
}

export function isStaffOverrideKind(value: string): value is StaffOverrideKind {
  return (STAFF_OVERRIDE_KINDS as readonly string[]).includes(value);
}

export function isStaffOverrideLockedKind(value: string): value is StaffOverrideLockedKind {
  return (STAFF_OVERRIDE_LOCKED_KINDS as readonly string[]).includes(value);
}

export function lockedReasonKey(kind: StaffOverrideLockedKind): string {
  return `staffOverride.locked.${kind}`;
}

/** A reopen reason is the client-facing sentence "partak ti to vratil k
 *  dopracovani: <reason>", so it must exist and stay printable. */
export function normalizeOverrideReason(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const reason = value.trim().replace(/\s+/g, " ");
  if (reason.length < 3 || reason.length > 500) return null;
  return reason;
}

export function parseStaffOverrides(value: unknown): StaffOverridesValue {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const record = value as Record<string, unknown>;
  const result: StaffOverridesValue = {};
  for (const kind of STAFF_OVERRIDE_KINDS) {
    const entry = record[kind];
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const candidate = entry as Record<string, unknown>;
    if ((candidate.action === "reopened" || candidate.action === "approved")
      && typeof candidate.reason === "string" && typeof candidate.at === "string") {
      result[kind] = {
        action: candidate.action,
        reason: candidate.reason,
        by: typeof candidate.by === "string" ? candidate.by : "",
        by_name: typeof candidate.by_name === "string" ? candidate.by_name : "",
        at: candidate.at
      };
    }
  }
  return result;
}
